import React, { useState, useEffect } from "react";
import { Loader2, History, Search, ArrowRight } from "lucide-react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import frameBg from "../../../../Assets/FrameBGDashboard.png";
import { UserHistory, GetAllUsers } from "../../AdminServices/AdminUserHistory";
import { useNavigate } from "react-router-dom";
import NotificationAlert from "../../../Alerts/NotificationAlert";


const AdminUserHistory = () => {
  const navigate = useNavigate();
  const [userUID, setUserUID] = useState("");
  const [users, setUsers] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoveredTask, setHoveredTask] = useState(null);

  const [alert, setAlert] = useState(null);

  const toLocalISO = (date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 19);

  const formatChangeValue = (field, value) => {
    if (!value) return "—";

    if (field === "Estimated Completion") {
      return formatDateTime(value);
    }

    return value;
  };

  const getChanges = (prev, curr) => {
    if (!prev) return [];

    const fields = [
      { key: "statusName", label: "Status" },
      { key: "severityName", label: "Severity" },
      { key: "assignedToName", label: "Assigned To" },
      { key: "estimatedComplete", label: "Estimated Completion" },
      { key: "remark", label: "Remark" },
    ];

    return fields
      .filter((f) => (prev[f.key] || "") !== (curr[f.key] || ""))
      .map((f) => ({
        field: f.label,
        from: formatChangeValue(f.label, prev[f.key]),
        to: formatChangeValue(f.label, curr[f.key]),
      }));
  };

  const handleSubmit = async () => {
    if (!userUID || !fromDate || !toDate) {
      setError("Please enter User UID and select both dates.");
      return;
    }

    setError("");
    setLoading(true);
    setHistoryList([]);

    try {
      const payload = {
        userUID,
        fromDate: toLocalISO(fromDate), // 00:00:00
        toDate: toLocalISO(toDate), // 23:59:59
      };

      const res = await UserHistory(payload);

      if (res.data.status === "Success") {
        const sorted = [...res.data.history].sort(
          (a, b) =>
            new Date(a.updatedAt || a.createdAt) -
            new Date(b.updatedAt || b.createdAt),
        );
        setHistoryList(sorted);
      } else {
        setError("No history found.");
      }
    } catch {
      setError("Something went wrong while loading history.");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) =>
    new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await GetAllUsers({});
        if (res.data?.status === "Success") {
          setUsers(res.data.data || []);
        } else {
          console.warn("API returned non-success:", res.data);
        }
      } catch (err) {
        console.error("Failed to load users", err);
        setAlert({
          type: "error",
          message: error?.response?.data?.message || "Failed to load user dropdown."
        })
      }
    };

    fetchUsers();
  }, []);

  return (
    <>
    <div className="min-h-screen w-full">
      {/* HEADER */}
      <div
        className="relative rounded-lg overflow-hidden shadow-md text-white mt-6"
        style={{
          backgroundImage: `url(${frameBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-8">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <History /> User Task History
          </h2>
        </div>
      </div>

      {/* FILTER */}
      <div className="p-6 md:p-10">
        <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-xl border p-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <select
              value={userUID}
              onChange={(e) => setUserUID(e.target.value)}
              className="p-3 border rounded-lg bg-white"
            >
              <option value="">Select User</option>

              {users.map((user) => (
                <option key={user.userUID} value={user.userUID}>
                  {user.name}
                </option>
              ))}
            </select>

            <Flatpickr
              value={fromDate && toDate ? [fromDate, toDate] : []}
              onChange={(selectedDates) => {
                if (selectedDates.length === 2) {
                  const start = new Date(selectedDates[0]);
                  start.setHours(0, 0, 0, 0);

                  const end = new Date(selectedDates[1]);
                  end.setHours(23, 59, 59, 999);

                  setFromDate(start);
                  setToDate(end);
                }
              }}
              options={{
                mode: "range",
                dateFormat: "Y-m-d",
                enableTime: false,
              }}
              className="p-3 border rounded-lg w-full"
              placeholder="Select From & To Date"
            />

            <button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white py-3 rounded-lg flex justify-center items-center gap-2 hover:scale-[1.02] transition"
            >
              <Search size={18} /> Fetch
            </button>
          </div>

          {error && <p className="text-red-500 mt-3 font-medium">{error}</p>}
        </div>

        {/* LOADER */}
        {loading && (
          <div className="flex justify-center mt-20">
            <Loader2 className="animate-spin text-[#0061A4]" size={42} />
          </div>
        )}

        {!loading && historyList.length === 0 && !error && (
          <div className="text-center text-gray-400 mt-24">
            <History size={36} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">
              No history available for selected date range
            </p>
          </div>
        )}

        {/* TIMELINE */}
        {!loading && historyList.length > 0 && (
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border p-8">
            <h3 className="text-xl font-bold text-[#0061A4] mb-8">
              History Timeline
            </h3>

            <div className="relative ml-6 space-y-10">
              <div className="absolute left-1 top-0 bottom-0 w-0.5 bg-[#0061A4]/30" />

              {historyList.map((item, index) => {
                const prev = historyList[index - 1];
                const changes = getChanges(prev, item);

                return (
                  <div key={index} className="relative pl-10">
                    <div className="absolute left-[-3px] top-2 w-4 h-4 bg-[#0061A4] rounded-full shadow" />

                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 p-5">
                      <div className="flex justify-between text-sm text-gray-500">
                        <div
                          className="relative inline-block"
                          onMouseEnter={() => setHoveredTask(index)}
                          onMouseLeave={() => setHoveredTask(null)}
                        >
                          <span className="font-semibold text-[#0061A4] cursor-pointer hover:underline">
                            {item.taskNumber}
                          </span>

                          {hoveredTask === index && (
                            <div className="absolute left-0 top-full mt-2 w-80 bg-white text-gray-800 text-xs border border-gray-200 rounded-lg shadow-xl p-3 z-50">
                              {/* Arrow */}
                              <div className="absolute -top-2 left-4 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />

                              {/* <div className="font-medium text-gray-700 mb-1">
                                Description
                              </div> */}

                              <div className="text-gray-600 leading-relaxed max-h-32 overflow-auto">
                                {item.description || "No description available"}
                              </div>
                            </div>
                          )}
                        </div>

                        <span>
                          {formatDateTime(item.updatedAt || item.createdAt)}
                        </span>
                      </div>

                      <p className="text-sm mt-1 text-gray-600">
                        {item.projectName} • Updated by{" "}
                        <b>{item.updatedByName || item.createdByName}</b>
                      </p>

                      {changes.length > 0 ? (
                        <div className="mt-4 space-y-2">
                          {changes.map((c, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 text-sm"
                            >
                              <span className="font-medium">{c.field}</span>
                              <span className="text-red-700 bg-red-100/70 px-2 py-0.5 rounded">
                                {c.from}
                              </span>
                              <ArrowRight size={14} />
                              <span className="text-green-700 bg-green-100/70 px-2 py-0.5 rounded">
                                {c.to}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-gray-400">
                          No changes detected
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>

    {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </>
    
  );
};

export default AdminUserHistory;
